import type { Event } from '@sentry/react'
import { isChunkLoadMessage } from './noiseRules'

// Bulk endpoints interpolate a comma-joined id list into one segment
const NUMERIC_SEGMENT = /^\d+(,\d+)*$/
const GUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
// No route in endpoints.ts contains a dot or a percent sign, so a segment carrying either
// is an interpolated value - a filename, say - not part of the path.
const OPAQUE_SEGMENT = /[%.]/

/**
 * Sentry groups by message text, so `/provider/123/comment` and `/provider/456/comment`
 * become separate issues. Collapse identifiers so one endpoint is one issue.
 */
export const normalizeEndpoint = (endpoint: string): string =>
  endpoint
    .split('?')[0]
    .split('/')
    .map(segment => {
      if (NUMERIC_SEGMENT.test(segment)) return '{id}'
      if (GUID_SEGMENT.test(segment)) return '{guid}'
      if (OPAQUE_SEGMENT.test(segment)) return '{param}'
      return segment
    })
    .join('/')

const tagOf = (event: Event, name: string): string | undefined => {
  const value = event.tags?.[name]
  return typeof value === 'string' ? value : undefined
}

export const collectMessages = (event: Event, originalException?: unknown): readonly string[] => {
  const messages = new Set<string>()

  if (event.message) {
    messages.add(event.message)
  }

  event.exception?.values?.forEach(value => {
    if (value.value) messages.add(value.value)
    if (value.type) messages.add(value.type)
  })

  let cause: unknown = originalException
  for (let depth = 0; cause && depth < 5; depth++) {
    if (typeof cause === 'string') {
      messages.add(cause)
      break
    }
    if (typeof cause !== 'object') break

    const asError = cause as { message?: unknown; cause?: unknown }
    if (typeof asError.message === 'string') messages.add(asError.message)
    cause = asError.cause
  }

  return [...messages]
}

/**
 * Returns a stable grouping key, or undefined to leave Sentry's stack-based
 * grouping alone (which is the better choice for real code defects).
 */
export const resolveFingerprint = (event: Event, messages: readonly string[]): readonly string[] | undefined => {
  if (event.fingerprint?.length) {
    return event.fingerprint
  }

  if (messages.some(isChunkLoadMessage)) {
    return ['chunk-load-failure']
  }

  const errorType = tagOf(event, 'errorType')

  if (errorType === 'api') {
    const endpoint = tagOf(event, 'endpoint')
    return [
      'api',
      tagOf(event, 'method') ?? 'unknown',
      endpoint ? normalizeEndpoint(endpoint) : 'unknown',
      tagOf(event, 'statusCode') ?? 'unknown',
    ]
  }

  if (errorType === 'signalr') {
    return ['signalr', tagOf(event, 'operation') ?? 'unknown']
  }

  if (errorType === 'pdf-export') {
    return ['pdf-export', tagOf(event, 'component') ?? 'unknown', tagOf(event, 'issue') ?? 'unknown']
  }

  return undefined
}
